<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/employees';
    import { computed } from 'vue';
    import { handleQueryRetry } from '@/utils/helpers';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateEmployee from '@/components/employees/CreateEmployee.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import ExportEmployeesChanges from '@/components/employees/ExportEmployeesChanges.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem } from '@/types';
    import UploadEmployees from '@/components/employees/UploadEmployee.vue';
    import UploadEmployeesAdmin from '@/components/employees/UploadEmployeeAdmin.vue';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const isClient = computed(() => page.props.auth.user.is_client);
    const canAccessSalary = computed(() => page.props.auth.user.can_access_salary);

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'employee.page_header',
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.EMPLOYEES, selectedCompanyId],
        queryFn: getEmployees,
        retry: handleQueryRetry,
        enabled: computed(() => !!page.props.auth.user.selected_company_id),
    });

    const { extractAndCleanSearch } = useUrlSearch();
    const initialSearch = extractAndCleanSearch();

    async function getEmployees() {
        const { data } = await axios.get(route('v2.salary.employees.table'), {
            params: {
                search: initialSearch || undefined,
            },
        });
        return data.data;
    }

    const showChangesColumn = computed(() => !(isClient.value && !canAccessSalary.value));
</script>

<template>
    <Head :title="$t('employee.page_header')" />

    <AppLayout
        :breadcrumbs="breadcrumbs"
        require-selected-company
    >
        <PageCard>
            <DataTable
                :table-key="QueryKey.EMPLOYEES"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :auto-url-search="true"
                :default-column-visibility="{
                    company_id: false,
                    changes: showChangesColumn,
                    actions: hasPermission(Permission.EMPLOYEES_UPDATE),
                }"
                :default-sorting="[
                    {
                        id: 'full_name',
                        desc: false,
                    },
                ]"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('employee.page_header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="employees">
                        <CreateEmployee v-if="hasPermission(Permission.EMPLOYEES_CREATE)" />
                        <UploadEmployees v-if="hasPermission(Permission.EMPLOYEES_IMPORT_CREATE)" />
                        <UploadEmployeesAdmin
                            v-if="hasPermission(Permission.EMPLOYEES_ADMIN_IMPORT_CREATE)"
                        />
                        <ExportEmployeesChanges v-if="hasPermission(Permission.EMPLOYEES_VIEW)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
